import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Select, Spin, Table, message } from "antd";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DataCard } from "../../shared/ui/DataCard";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { extractErrorMessage } from "../../shared/api/axios";
import { fetchColletoLockers, fetchReservasByColleto } from "./api";

const reservasColumns = [
  {
    title: "Reserva",
    dataIndex: "reservationId",
    key: "reservationId",
    render: (value) => value || "N/A",
  },
  {
    title: "Locker",
    dataIndex: "lockerNumber",
    key: "lockerNumber",
    render: (value) => value || "N/A",
  },
  {
    title: "Status",
    key: "status",
    render: () => <span className="te-status-pill">Pendente</span>,
  },
];

export default function Reservas() {
  const isMobile = useIsMobile();
  const [colletoId, setColletoId] = useState();

  const lockersQuery = useQuery({
    queryKey: ["lockers-colleto"],
    queryFn: fetchColletoLockers,
  });

  const query = useQuery({
    queryKey: ["reservas", colletoId],
    queryFn: () => fetchReservasByColleto(colletoId),
    enabled: Boolean(colletoId),
  });

  const queryError = lockersQuery.error || query.error;
  if (queryError) {
    message.error(extractErrorMessage(queryError, "Erro ao buscar reservas."));
  }

  const reservas = query.data || [];
  const lockerOptions = (lockersQuery.data || []).map((locker) => ({
    value: locker.colletoId,
    label: locker.name,
  }));
  const lockerName = lockerOptions.find((option) => option.value === colletoId)?.label;

  return (
    <div className="te-page-wrap te-stack">
      <PageHeader
        title="Reservas"
        subtitle="Consulte rapidamente as reservas pendentes de um locker."
        metricLabel="Reservas encontradas"
        metricValue={reservas.length}
      />

      <Card className="te-card">
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ margin: 0, color: "var(--te-primary)" }}>Locker</h3>
          <p className="te-kicker">Escolha o locker para ver as reservas pendentes.</p>
        </div>

        <div
          className="te-split-grid te-split-grid--two"
          style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)" }}
        >
          <div style={{ width: "100%", minWidth: 0 }}>
            <label className="te-label">Locker</label>
            <Select
              size="large"
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              placeholder="Selecione o locker"
              loading={lockersQuery.isLoading}
              options={lockerOptions}
              value={colletoId}
              onChange={setColletoId}
            />
          </div>

          <div style={{ width: "100%", minWidth: 0 }}>
            <label className="te-label">Resumo</label>
            <div className="te-field-box">
              {lockerName
                ? `${reservas.length} reservas em ${lockerName}`
                : `${reservas.length} reservas`}
            </div>
          </div>
        </div>
      </Card>

      <Card className="te-card">
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: "var(--te-primary)" }}>Lista de reservas</h3>
          <p className="te-kicker">Acompanhe as reservas pendentes do locker selecionado.</p>
        </div>

        {query.isFetching ? (
          <div className="te-empty">
            <Spin />
          </div>
        ) : reservas.length === 0 ? (
          <div className="te-empty">
            {lockerName
              ? `Nenhuma reserva pendente em ${lockerName}.`
              : "Selecione um locker para ver as reservas."}
          </div>
        ) : isMobile ? (
          <div className="te-stack">
            {reservas.map((reserva, index) => (
              <DataCard
                key={`${reserva.reservationId || "reserva"}-${index}`}
                title="Reserva"
                badge={<span className="te-status-pill">Pendente</span>}
                rows={[
                  { label: "ID", value: reserva.reservationId || "N/A" },
                  { label: "Locker", value: reserva.lockerNumber || "N/A" },                ]}
              />
            ))}
          </div>
        ) : (
          <Table
            dataSource={reservas}
            columns={reservasColumns}
            rowKey={(record, index) => `${record.reservationId || "reserva"}-${index}`}
            pagination={{ pageSize: 8, showSizeChanger: false }}
          />
        )}
      </Card>
    </div>
  );
}
